<template>
  <section>
    <!-- Video Player -->
    <div class="mx-auto max-w-7xl md:px-6 lg:px-8">
      <div ref="player" class="aspect-video bg-gray-900 rounded-md shadow-lg">
        <div class="absolute inset-0 grid animate-pulse place-items-center">
          <span class="flex flex-col items-center gap-y-2">
            <svg class="size-8 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            <span class="text-xl uppercase text-gray-400">
              Your video is loading ...
            </span>
          </span>
        </div>
      </div>

      <div v-if="currentVideo" class="flex justify-between gap-x-4 px-4 max-lg:flex-col mt-6 md:px-0">
        <h1 class="text-3xl text-white">{{ currentVideo.title }}</h1>
        <div class="flex items-center gap-4 uppercase max-lg:justify-between">
          <p v-if="currentVideo.timeLength" class="flex items-center gap-2 whitespace-nowrap text-gray-500">
            <SvgIcon name="time-fill" classes="size-6" />
            <span>{{ currentVideo.timeLength }}</span>
          </p>

          <p v-if="hasPlaylist" class="flex gap-2 whitespace-nowrap underline-offset-2 text-gray-500">
            <SvgIcon name="play-list-2-fill" classes="size-6" />
            <span>{{ playlist.length }} Videos</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Playlist -->
    <div v-if="hasPlaylist" ref="slider" class="swiper mt-6 !px-4 md:!px-8 xl:!px-[calc((100%-1280px)/2+2rem)]">
      <div class="max-w-7xl mx-auto flex justify-between items-center mb-4 px-4">
        <button class="text-white button-prev flex items-center uppercase tracking-wider font-medium text-sm">
          <svgIcon name="arrow-drop-left-fill"></svgIcon>
          <span>Previous</span>
        </button>
        <button class="button-next text-white flex items-center uppercase tracking-wider font-medium text-sm">
          <span>Next</span>
          <svgIcon name="arrow-drop-right-fill"></svgIcon>
        </button>
      </div>

      <ol class="swiper-wrapper !overflow-visible pl-0">
        <li v-for="(video) in playlist" class="swiper-slide relative mb-2 pb-2"
          :class="{ 'playing-video': video.jwMediaId === currentVideo?.jwMediaId }">
          <figure class="relative">
            <img :src="video.posterUrl"
              class="rounded-lg border-2 border-transparent [.playing-video_&]:border-teal-500" alt="" />
            <div
              class="absolute inset-0 hidden place-content-center rounded-lg bg-gradient-to-t from-teal-500 [.playing-video_&]:grid">
              <SvgIcon name="play-fill-shadow" classes="text-white size-24" />
            </div>

            <span v-if="video.timeLength"
              class="absolute bottom-3 right-3 flex items-center gap-x-1 rounded-md bg-white/60 px-2 py-0.5 text-sm text-gray-950 backdrop-blur-sm">
              <SvgIcon name="time-fill" classes="text-gray-950 size-4" />
              {{ video.timeLength }}
            </span>
          </figure>

          <button @click="loadPlaylistVideo(video.jwMediaId)"
            class="mt-2 block cursor-pointer text-white decoration-slate-300 decoration-1 underline-offset-2 hover:text-white"
            type="button">
            <span class="absolute inset-0"></span>
            <span class="text-xl text-white">{{ video.title }}</span>
          </button>
        </li>
      </ol>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, toRaw, computed } from 'vue';
import Swiper, { Navigation } from 'swiper';
import 'swiper/css'
import 'swiper/css/navigation';
import SvgIcon from '@js/components/SvgIcon.vue';

const { playlist } = defineProps({
  playlist: Array
})
const slider = ref(null);
const player = ref(null);
const currentVideo = ref(null);
const hasPlaylist = computed(() => playlist && playlist.length > 1);
let playerInstance = null;

onMounted(() => {
  // Set current video to the first video in the playlist
  currentVideo.value = playlist[0];
  setUpPlayer();

  // Only run the code below if there is a playlist
  if (!hasPlaylist) return;

  const swiper = new Swiper(slider.value, {
    modules: [Navigation],
    slidesPerView: 1.25,
    spaceBetween: 16,
    noSwipingSelector: '.swiper-slide',
    grabCursor: true,
    navigation: {
      nextEl: '.button-next',
      prevEl: '.button-prev',
      disabledClass: 'opacity-50'
    },
    breakpoints: {
      640: {
        slidesPerView: 2.25,
      },
      768: {
        slidesPerView: 3.25,
      },
    }
  })

  // Play next video when current video ends
  playerInstance.on('complete', () => {
    const currIndex = playlist.indexOf(toRaw(currentVideo.value));

    if (currIndex === -1 || currIndex + 1 > playlist.length) return;

    const nextVideo = playlist[currIndex + 1];
    nextVideo && loadPlaylistVideo(nextVideo.jwMediaId);
  });
})

function setUpPlayer() {
  const url = `https://content.jwplatform.com/v2/media/${currentVideo.value.jwMediaId}?format=json&poster_width=1920`;

  window.jwplayer.key = window.gembaConfig.jwKey;

  const playerConfig = {
    playlist: url,
    playbackRateControls: true,
    playbackRates: [0.85, 1, 1.25, 1.5, 1.75, 2],
    preload: 'auto',
    width: '100%',
    aspectratio: '16:9',
    floating: { dismissible: true },
    abouttext: `All Content Copyright ${new Date().getFullYear()} Gemba Academy Inc.`,
    aboutlink: "https://www.gembaacademy.com/support",
    skin: {
      "controlbar": { "iconsActive": "#05B284" },
      "menus": { "textActive": "#05B284" }
    },
    displaydescription: false,
    displaytitle: false,
  }
  playerInstance = window.jwplayer(player.value)?.setup(playerConfig);

  playerInstance.on('error', (error) => {
    console.error('JW Player Error:', error);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Video Playback Error',
      'eventCategory': 'JW Video Errors',
      'eventAction': 'LL Errors',
      'eventLabel': error.code
    });
  });

  playerInstance.on('firstFrame', function () {
    const currCaption = playerInstance.getCurrentCaptions()
    const captionList = playerInstance.getCaptionsList()
    const captionLabel = captionList[currCaption].label

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Video Captioning',
      'eventCategory': 'LL Caption',
      'eventAction': 'Initial Caption',
      'eventLabel': captionLabel
    });
  });

  playerInstance.on('captionsChanged', function () {
    const currCaption = playerInstance.getCurrentCaptions()
    const captionList = playerInstance.getCaptionsList()
    const captionLabel = captionList[currCaption].label

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Video Captioning',
      'eventCategory': 'LL Caption',
      'eventAction': 'Caption Changed',
      'eventLabel': captionLabel
    });
  });

  playerInstance.on('play', function () {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'eventAction': 'Play Video',
      'eventLabel': currentVideo.value.title
    });
  });
}

function loadPlaylistVideo(mediaId) {
  playerInstance.load(`https://content.jwplatform.com/v2/media/${mediaId}?format=json&poster_width=1920`);
  playerInstance.play();
  currentVideo.value = playlist.find(video => video.jwMediaId === mediaId);
}

</script>
